import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import styled from 'styled-components';
import { calculateChange } from './change_calculations';
import { calculateSummary } from './summary_calculations';
import { calculateSummary as calculateManagerReport } from './manager_report';
import { calculateSummary as calculateManagerReportV2 } from './manager_v2';
import { calculateSummary as calculateManagerReportV3 } from './manager_v3';
import { calculateOrganizationReport } from './organization_report';
import { calculateOrganizationReportManager } from './organization_report_manager';
import { calculateOrganizationReportWithDemographics } from './organization_report_demo';
import { SG_GET_CULTURE_AUDIT_REPORTS, SG_GET_ORGANIZATION_GROUP, LOAD_EMPLOYEES, SG_FETCH_ORGANIZATION_GROUP } from 'constants/actions';
import { SURVEY_TYPE } from 'constants/surveys';

const Container = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Raleway', sans-serif;
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #2A3039;
  margin: 0 0 8px 0;
  font-family: 'Raleway', sans-serif;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #666D79;
  margin: 0;
  font-weight: 400;
`;

const ControlsSection = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #2A3039;
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #DFDFDF;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Raleway', sans-serif;
  background: white;
  color: #2A3039;
  min-width: 200px;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #27CDA7;
  }

  &:focus {
    outline: none;
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
  }

  &:disabled {
    background: #F2F2F2;
    cursor: not-allowed;
    color: #B3B3B3;
  }
`;

const TriggerButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #27CDA7 0%, #2D70E2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  min-height: 44px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(45, 112, 226, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #B3B3B3;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ResultsSection = styled.div`
  margin-top: 24px;
`;

const ResultsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const ResultsTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #2A3039;
  margin: 0;
`;

const CopyButton = styled.button`
  padding: 8px 16px;
  background: white;
  color: #2D70E2;
  border: 2px solid #2D70E2;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  font-family: 'Raleway', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2D70E2;
    color: white;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const JsonContainer = styled.div`
  background: #F8FAFF;
  border: 1px solid #DFDFDF;
  border-radius: 8px;
  padding: 20px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  color: #2A3039;
  overflow-x: auto;
  max-height: 500px;
  overflow-y: auto;
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px;
  
  &::after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid #DFDFDF;
    border-top: 3px solid #27CDA7;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: #FEF3F4;
  border: 1px solid #F15062;
  border-radius: 8px;
  padding: 16px;
  color: #F15062;
  font-size: 14px;
  margin-top: 16px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #666D79;
  font-size: 14px;
`;

const StatusText = styled.p`
  font-size: 12px;
  color: #666D79;
  margin-top: 8px;
  font-style: italic;
`;

const TokenUsageDisplay = styled.div`
  background: #F0F7FF;
  border: 2px solid #2D70E2;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  color: #2A3039;
`;

const TokenUsageTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #2D70E2;
  font-family: 'Raleway', sans-serif;
`;

const TokenRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  
  &:last-child {
    margin-bottom: 0;
    padding-top: 8px;
    border-top: 1px solid #DFDFDF;
    font-weight: 600;
  }
`;

const TokenLabel = styled.span`
  color: #666D79;
`;

const TokenValue = styled.span`
  color: #2A3039;
  font-weight: 600;
`;

const CostValue = styled.span`
  color: #27CDA7;
  font-weight: 600;
  font-size: 13px;
`;

const PricingNote = styled.div`
  font-size: 10px;
  color: #666D79;
  margin-top: 8px;
  font-style: italic;
  border-top: 1px solid #DFDFDF;
  padding-top: 8px;
`;

const ReportV2 = () => {
    const dispatch = useDispatch();
    const [selectedCalculation, setSelectedCalculation] = useState('changeAnalysis');
    const [selectedAnchorParent, setSelectedAnchorParent] = useState('');
    const [selectedModel, setSelectedModel] = useState('gpt-4.1');
    const [selectedAnchors, setSelectedAnchors] = useState([]);
    const [combineAnchors, setCombineAnchors] = useState(false);
    const [selectedManagerId, setSelectedManagerId] = useState('');
    const [calculationResult, setCalculationResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [tokenUsage, setTokenUsage] = useState({
        prompt_tokens: 0,
        completion_tokens: 0,
        total_tokens: 0,
        call_count: 0,
        input_cost: 0,
        output_cost: 0,
        total_cost: 0
    });

    const core_data = useSelector(
        (state) => state.audit?.core_data,
        shallowEqual
    );

    const surveyStructure = useSelector(
        (state) => state.surveystructure?.survey_structure,
        shallowEqual
    );

    console.log("core_data", core_data);
    const raw_data = useSelector((state) => state.audit?.raw_data, shallowEqual);
    const debriefSchedules = useSelector((state) => state.debrief_schedule, shallowEqual);
    const { organization_groups, groupsPending, employees, organization_group_detail } = useSelector(
        (state) => ({
            organization_groups: Array.isArray(state.organization_group?.organization_groups)
                ? state.organization_group.organization_groups
                : [],
            groupsPending: state.organization_group?.pending,
            employees: Array.isArray(state.employees?.employees) ? state.employees.employees : [],
            organization_group_detail: state.organization_group?.organization_group,
        }),
        shallowEqual
    );

    // Organization + culture audit reports state for triggering fetch on refresh
    const {
        organizationId,
        cultureAuditReports,
        reportsPending,
    } = useSelector(
        (state) => ({
            organizationId:
                state.selectedOrg?.organization?.id ||
                Number(state.auth?.organization_id),
            cultureAuditReports: state.debrief_schedule?.culture_audit_reports,
            reportsPending: state.debrief_schedule?.reportsPending,
        }),
        shallowEqual
    );

    // Track if we've already requested culture audit reports once for this component mount.
    // This prevents repeatedly re-dispatching on persistent failures (e.g. CORS errors),
    // which would otherwise hammer the `/report/culture-audit` endpoint.
    const hasRequestedCultureAuditRef = useRef(false);

    // Fetch organization groups (permission groups) for manager selection
    useEffect(() => {
        const orgId = Number(organizationId);
        if (!orgId || Number.isNaN(orgId) || orgId <= 0) return;
        dispatch({
            type: SG_GET_ORGANIZATION_GROUP,
            payload: {
                page: 1,
                page_size: 200,
                organization: orgId,
                group_type: 'permission',
            },
        });
    }, [dispatch, organizationId]);

    // Ensure employees are available for manager names
    useEffect(() => {
        if (!employees || employees.length === 0) {
            dispatch({ type: LOAD_EMPLOYEES });
        }
    }, [dispatch, employees]);

    // Ensure culture audit reports are loaded when landing directly on this page (e.g. hard refresh)
    useEffect(() => {
        const orgId = Number(organizationId);

        if (!orgId || Number.isNaN(orgId) || orgId <= 0) {
            return;
        }

        const hasReports =
            Array.isArray(cultureAuditReports?.response) &&
            cultureAuditReports.response.length > 0;

        if (hasReports || reportsPending || hasRequestedCultureAuditRef.current) {
            return;
        }

        hasRequestedCultureAuditRef.current = true;

        dispatch({
            type: SG_GET_CULTURE_AUDIT_REPORTS,
            payload: {
                auditTypeId: SURVEY_TYPE.AUDIT,
                organization: orgId,
            },
        });
    }, [dispatch, organizationId, cultureAuditReports, reportsPending]);

    console.log("raw_data", raw_data);
    // Get anchor parents from core_data
    const anchorParents = core_data?.categories?.[0]?.categories || [];

    // Model options with pricing
    const modelOptions = [
        {
            value: 'gpt-4.1',
            label: 'GPT-4.1',
            inputCostPerMillion: 2.00,
            outputCostPerMillion: 8.00
        },
        {
            value: 'gpt-4.1-mini',
            label: 'GPT-4.1 Mini',
            inputCostPerMillion: 0.40,
            outputCostPerMillion: 1.60
        },
        {
            value: 'gpt-4.1-nano',
            label: 'GPT-4.1 Nano',
            inputCostPerMillion: 0.10,
            outputCostPerMillion: 0.40
        },
        {
          value: 'gpt-5-nano',
          label: 'GPT-5 Nano',
          inputCostPerMillion: 0.05,
          outputCostPerMillion: 0.40
      },
      {
          value: 'gpt-5-mini',
          label: 'GPT-5 Mini',
          inputCostPerMillion: 0.25,
          outputCostPerMillion: 2.00
      },
        {
            value: 'grok-4-latest',
            label: 'Grok 4',
            inputCostPerMillion: 3.00,
            outputCostPerMillion: 15.00
        }
    ];

    // Get current model pricing
    const currentModel = modelOptions.find(model => model.value === selectedModel);

    // Set default anchor parent if not selected and data is available
    useEffect(() => {
        if (anchorParents.length > 0 && !selectedAnchorParent) {
            setSelectedAnchorParent(anchorParents[0].id);
        }
    }, [anchorParents, selectedAnchorParent]);

    // Reset selected anchors when parent changes
    useEffect(() => {
        setSelectedAnchors([]);
    }, [selectedAnchorParent]);

    const calculationOptions = [
        { value: 'changeAnalysis', label: 'Change Analysis', func: calculateChange },
        { value: 'summaryAnalysis', label: 'Summary Analysis', func: calculateSummary },
        { value: 'managerReport', label: 'Manager Report', func: calculateManagerReport },
        { value: 'managerReportV2', label: 'Manager Report V2', func: calculateManagerReportV2 },
        { value: 'managerReportV3', label: 'Manager Report V3', func: calculateManagerReportV3 },
        { value: 'organizationReport', label: 'Organization Report', func: calculateOrganizationReport },
        { value: 'organizationReportManager', label: 'Organization Report (Manager edition)', func: calculateOrganizationReportManager },
        {
            value: 'organizationReportWithDemographics',
            label: 'Organization Report (with demographics)',
            func: (raw, core, deb, anchorParent, tokenCb, model, selectedAnchorsArg, combineAnchorsArg) =>
                calculateOrganizationReportWithDemographics(
                    raw,
                    core,
                    deb,
                    anchorParent,
                    tokenCb,
                    model,
                    selectedAnchorsArg,
                    combineAnchorsArg,
                    surveyStructure
                )
        }
    ];

    const canCalculate = raw_data && core_data && selectedAnchorParent;

    const formatManagerName = (managerObj, fallbackId) => {
        if (managerObj && typeof managerObj === 'object') {
            const fullName = `${managerObj.first_name || ''} ${managerObj.last_name || ''}`.trim();
            if (fullName) return fullName;
            if (managerObj.email) return managerObj.email;
        }
        const matchEmp = employees?.find((e) => String(e.id) === String(fallbackId));
        if (matchEmp) {
            const fullName = `${matchEmp.first_name || ''} ${matchEmp.last_name || ''}`.trim();
            return fullName || matchEmp.email || `Manager ${fallbackId}`;
        }
        return `Manager ${fallbackId}`;
    };

    const managerOptions = useMemo(() => {
        const seen = new Set();
        return (organization_groups || []).reduce((acc, group) => {
            const managerId =
                typeof group.manager === 'object' ? group.manager?.id : group.manager;
            if (!managerId || seen.has(managerId)) return acc;
            seen.add(managerId);
            const managerObj =
                typeof group.manager === 'object'
                    ? group.manager
                    : employees?.find((e) => String(e.id) === String(managerId));
            const label = formatManagerName(managerObj, managerId);
            acc.push({
                value: String(managerId),
                label,
                groupId: group.id,
                membersCount: Array.isArray(group.members) ? group.members.length : 0,
            });
            return acc;
        }, []);
    }, [organization_groups, employees]);

    const selectedManagerGroup = useMemo(() => {
        if (!selectedManagerId) return null;
        return (organization_groups || []).find((group) => {
            const managerId =
                typeof group.manager === 'object' ? group.manager?.id : group.manager;
            return String(managerId) === String(selectedManagerId);
        });
    }, [organization_groups, selectedManagerId]);

    // Prefer detailed group (with members) when available, mirroring GroupDetail logic
    const selectedManagerGroupWithMembers = useMemo(() => {
        if (!selectedManagerGroup) return null;
        if (
            organization_group_detail &&
            organization_group_detail.id === selectedManagerGroup.id &&
            Array.isArray(organization_group_detail.members)
        ) {
            return organization_group_detail;
        }
        return selectedManagerGroup;
    }, [selectedManagerGroup, organization_group_detail]);

    // Ensure we fetch full group details (including members) when a manager is selected
    useEffect(() => {
        if (!selectedManagerGroup || !selectedManagerGroup.id) return;
        dispatch({
            type: SG_FETCH_ORGANIZATION_GROUP,
            payload: { id: selectedManagerGroup.id },
        });
    }, [dispatch, selectedManagerGroup && selectedManagerGroup.id]);

    const selectedMemberIds = useMemo(() => {
        const groupForMembers = selectedManagerGroupWithMembers;
        if (!groupForMembers || !Array.isArray(groupForMembers.members)) return [];

        return groupForMembers.members
            .map((member) => {
                if (typeof member === 'object' && member !== null) {
                    // Support various member shapes: { id }, { employee_id }, { employeeId }, { employee }, or raw id
                    if (member.id != null) return member.id;
                    if (member.employee_id != null) return member.employee_id;
                    if (member.employeeId != null) return member.employeeId;
                    if (member.employee != null) {
                        // member.employee may be an id or an object with id
                        if (typeof member.employee === 'object' && member.employee !== null) {
                            return member.employee.id ?? null;
                        }
                        return member.employee;
                    }
                    return null;
                }
                return member;
            })
            .filter((id) => id !== null && id !== undefined)
            .map((id) => Number(id));
    }, [selectedManagerGroupWithMembers]);

    const handleCalculate = async () => {
        if (!canCalculate) return;

        setLoading(true);
        setError(null);
        setCalculationResult(null);
        // Reset token usage
        setTokenUsage({
            prompt_tokens: 0,
            completion_tokens: 0,
            total_tokens: 0,
            call_count: 0,
            input_cost: 0,
            output_cost: 0,
            total_cost: 0
        });

        try {
            // Find the selected calculation function
            const selectedOption = calculationOptions.find(option => option.value === selectedCalculation);
            if (!selectedOption) {
                throw new Error('Invalid calculation type selected');
            }

            // Create token update callback with cost calculation
            const handleTokenUpdate = (newTokenUsage) => {
                // Calculate costs using selected model pricing
                const inputCost = (newTokenUsage.prompt_tokens / 1000000) * currentModel.inputCostPerMillion;
                const outputCost = (newTokenUsage.completion_tokens / 1000000) * currentModel.outputCostPerMillion;
                const totalCost = inputCost + outputCost;

                setTokenUsage({
                    ...newTokenUsage,
                    input_cost: inputCost,
                    output_cost: outputCost,
                    total_cost: totalCost
                });
            };

            const memberIdSet =
                selectedMemberIds && selectedMemberIds.length > 0
                    ? new Set(selectedMemberIds.map((id) => Number(id)))
                    : null;

            const extractEmployeeId = (entry) => {
                if (!entry) return null;
                const possible =
                    entry.employee_id ??
                    entry.employeeId ??
                    entry.emp_id ??
                    entry.empId ??
                    entry.employee?.id ??
                    entry.employee ??
                    entry.user_id ??
                    entry.userId ??
                    entry.respondent_id ??
                    entry.respondentId ??
                    null;
                return possible !== null && possible !== undefined ? Number(possible) : null;
            };

            const filterByMembers = (list) => {
                if (!Array.isArray(list) || !memberIdSet) return list || [];
                return list.filter((item) => {
                    const empId = extractEmployeeId(item);
                    return empId !== null && memberIdSet.has(empId);
                });
            };

            let managerData = null;
            if (selectedManagerId) {
                const filteredResponses = filterByMembers(raw_data?.[0]?.responses || []);
                const filteredLast = filterByMembers(raw_data?.[0]?.last || []);
                const managerName = formatManagerName(
                    selectedManagerGroup?.manager,
                    selectedManagerId
                );
                managerData = {
                    manager_id: Number(selectedManagerId),
                    manager_name: managerName,
                    group_id: selectedManagerGroup?.id || null,
                    group_name: selectedManagerGroup?.name || null,
                    member_ids: selectedMemberIds,
                    responses: filteredResponses,
                    last: filteredLast,
                    source: 'manager_group_filter',
                };

                console.log('[ReportV2] Manager filter debug', {
                    selectedManagerId,
                    selectedMemberIdsCount: selectedMemberIds.length,
                    sampleMemberIds: selectedMemberIds.slice(0, 5),
                    rawResponsesCount: Array.isArray(raw_data?.[0]?.responses) ? raw_data[0].responses.length : 0,
                    filteredResponsesCount: Array.isArray(filteredResponses) ? filteredResponses.length : 0
                });
            }

            // Pass the selected anchor parent ID, selected model, and token callback to the calculation function
            const result = await selectedOption.func(
                raw_data,
                core_data,
                debriefSchedules,
                selectedAnchorParent,
                handleTokenUpdate,
                selectedModel,
                selectedAnchors,
                combineAnchors,
                managerData
            );
            setCalculationResult(result);
        } catch (err) {
            console.error('Calculation error:', err);
            setError(err.message || 'An error occurred during calculation');
        } finally {
            setLoading(false);
        }
    };

    const handleCopyToClipboard = async () => {
        if (!calculationResult) return;

        try {
            await navigator.clipboard.writeText(JSON.stringify(calculationResult, null, 2));
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    };

    return (
        <Container>
            <Header>
                <Title>Report Analysis v2</Title>
                <Subtitle>Select a calculation type and anchor parent to generate detailed analysis results</Subtitle>
            </Header>

            <ControlsSection>
                <ControlGroup>
                    <Label htmlFor="calculation-select">Calculation Type</Label>
                    <Select 
                        id="calculation-select"
                        value={selectedCalculation}
                        onChange={(e) => setSelectedCalculation(e.target.value)}
                        disabled={loading}
                    >
                        {calculationOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                </ControlGroup>

                <ControlGroup>
                    <Label htmlFor="anchor-parent-select">Anchor Parent</Label>
                    <Select 
                        id="anchor-parent-select"
                        value={selectedAnchorParent}
                        onChange={(e) => setSelectedAnchorParent(e.target.value)}
                        disabled={loading || anchorParents.length === 0}
                    >
                        {anchorParents.length === 0 ? (
                            <option value="">No anchor parents available</option>
                        ) : (
                            anchorParents.map(parent => (
                                <option key={parent.id} value={parent.id}>
                                    {parent.name || `Anchor Parent ${parent.id}`}
                                </option>
                            ))
                        )}
                    </Select>
                </ControlGroup>

                <ControlGroup>
                    <Label htmlFor="manager-select">Manager (optional)</Label>
                    <Select
                        id="manager-select"
                        value={selectedManagerId}
                        onChange={(e) => setSelectedManagerId(e.target.value)}
                        disabled={loading || groupsPending}
                    >
                        <option value="">All managers (no filter)</option>
                        {managerOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label} {opt.membersCount ? `• ${opt.membersCount} members` : ''}
                            </option>
                        ))}
                    </Select>
                </ControlGroup>

                {/* Anchors multi-select */}
                <ControlGroup>
                    <Label htmlFor="anchors-select">Anchors (optional)</Label>
                    <Select
                        id="anchors-select"
                        multiple
                        value={selectedAnchors.map(String)}
                        onChange={(e) => {
                            const parentObj = anchorParents.find(p => String(p.id) === String(selectedAnchorParent));
                            const children = parentObj?.options || [];
                            const values = Array.from(e.target.selectedOptions).map(o => o.value);
                            const typedIds = values
                                .map(v => children.find(ch => String(ch.id) === String(v))?.id)
                                .filter(v => v !== undefined && v !== null);
                            setSelectedAnchors(typedIds);
                        }}
                        disabled={loading || !selectedAnchorParent}
                        style={{ minHeight: 120 }}
                    >
                        {(anchorParents.find(p => String(p.id) === String(selectedAnchorParent))?.options || []).map(child => (
                            <option key={child.id} value={String(child.id)}>
                                {child.name || `Anchor ${child.id}`}
                            </option>
                        ))}
                    </Select>
                </ControlGroup>

                <ControlGroup>
                    <Label htmlFor="combine-anchors">Combine selected anchors</Label>
                    <input
                        id="combine-anchors"
                        type="checkbox"
                        checked={combineAnchors}
                        onChange={(e) => setCombineAnchors(e.target.checked)}
                        disabled={loading || selectedAnchors.length < 2}
                    />
                </ControlGroup>

                <ControlGroup>
                    <Label htmlFor="model-select">Model</Label>
                    <Select 
                        id="model-select"
                        value={selectedModel}
                        onChange={(e) => setSelectedModel(e.target.value)}
                        disabled={loading}
                    >
                        {modelOptions.map(model => (
                            <option key={model.value} value={model.value}>
                                {model.label}
                            </option>
                        ))}
                    </Select>
                </ControlGroup>

                <TriggerButton 
                    onClick={handleCalculate}
                    disabled={!canCalculate || loading}
                >
                    {loading ? 'Calculating...' : 'Run Calculation'}
                </TriggerButton>
            </ControlsSection>

            {(loading || tokenUsage.call_count > 0) && (
                <TokenUsageDisplay>
                    <TokenUsageTitle>
                        🤖 LLM Token Usage {loading && '(Real-time)'}
                    </TokenUsageTitle>
                    <TokenRow>
                        <TokenLabel>API Calls:</TokenLabel>
                        <TokenValue>{tokenUsage.call_count}</TokenValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Input Tokens:</TokenLabel>
                        <TokenValue>{tokenUsage.prompt_tokens.toLocaleString()}</TokenValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Output Tokens:</TokenLabel>
                        <TokenValue>{tokenUsage.completion_tokens.toLocaleString()}</TokenValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Total Tokens:</TokenLabel>
                        <TokenValue>{tokenUsage.total_tokens.toLocaleString()}</TokenValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Input Cost:</TokenLabel>
                        <CostValue>${tokenUsage.input_cost.toFixed(4)}</CostValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Output Cost:</TokenLabel>
                        <CostValue>${tokenUsage.output_cost.toFixed(4)}</CostValue>
                    </TokenRow>
                    <TokenRow>
                        <TokenLabel>Total Cost:</TokenLabel>
                        <CostValue>${tokenUsage.total_cost.toFixed(4)}</CostValue>
                    </TokenRow>
                    <PricingNote>
                        {currentModel.label} Pricing: ${currentModel.inputCostPerMillion} per 1M input tokens • ${currentModel.outputCostPerMillion} per 1M output tokens
                    </PricingNote>
                </TokenUsageDisplay>
            )}

            {!canCalculate && (
                <StatusText>
                    Waiting for required data... 
                    {!raw_data && 'Missing raw data. '}
                    {!core_data && 'Missing core data. '}
                    {!selectedAnchorParent && 'Please select an anchor parent.'}
                </StatusText>
            )}

            {error && (
                <ErrorMessage>
                    <strong>Error:</strong> {error}
                </ErrorMessage>
            )}

            {loading && <LoadingSpinner />}

            {calculationResult && (
                <ResultsSection>
                    <ResultsHeader>
                        <ResultsTitle>Calculation Results</ResultsTitle>
                        <CopyButton onClick={handleCopyToClipboard}>
                            {copySuccess ? '✓ Copied!' : 'Copy JSON'}
                        </CopyButton>
                    </ResultsHeader>
                    <JsonContainer>
                        <pre>{JSON.stringify(calculationResult, null, 2)}</pre>
                    </JsonContainer>
                </ResultsSection>
            )}

            {!calculationResult && !loading && !error && canCalculate && (
                <EmptyState>
                    Select a calculation type and anchor parent, then click "Run Calculation" to see results
                </EmptyState>
            )}
        </Container>
    );
};

export default ReportV2;